import { Head, Link, router, useForm } from "@inertiajs/react"
import { CheckCircle, Clock, Plus, Shield, Trash2, XCircle } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { proxyUrlPath, proxyUrlsPath } from "@/routes" // Added proxyUrlPath
import type { BreadcrumbItem } from "@/types"
import type { ProxyPlatformState, ProxyUrl } from "@/types"

interface ProxyUrlIndexProps {
  proxy_urls: ProxyUrl[]
  proxy_urls_by_provider: Record<string, ProxyUrl[]>
  stats: {
    total_count: number
    provider_counts: Record<string, number>
    priority_counts: Record<string, number>
    recent_count: number
  }
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Proxy URLs",
    href: proxyUrlsPath(),
  },
]

const PRIORITY_OPTIONS = [
  { value: "0", label: "Default (0)" },
  { value: "10", label: "High (10)" },
]

export default function ProxyUrlsIndex({
  proxy_urls,
  proxy_urls_by_provider,
  stats,
}: ProxyUrlIndexProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

  const { data, setData, post, processing, errors, reset } = useForm({
    url: "",
    provider: "",
    priority: 0,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(proxyUrlsPath(), {
      onSuccess: () => {
        reset()
        setIsAddDialogOpen(false)
      },
    })
  }

  const handleDelete = (proxyId: number) => {
    if (confirm("Are you sure you want to delete this proxy URL?")) {
      router.delete(`/proxy_urls/${proxyId}`, {
        onSuccess: () => {
          // Optionally show a success message
        },
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (states: ProxyPlatformState[]) => {
    if (states.length === 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="cursor-help text-green-600">
              <CheckCircle className="mr-1 h-3 w-3" />
              Fresh
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            This proxy has never been used and is ready for any platform
          </TooltipContent>
        </Tooltip>
      )
    }

    const bannedStates = states.filter((s) => s.status === "banned")

    if (bannedStates.length > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="destructive" className="cursor-help">
              <XCircle className="mr-1 h-3 w-3" />
              Banned ({bannedStates.length})
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            This proxy is banned on {bannedStates.length} platform(s)
          </TooltipContent>
        </Tooltip>
      )
    }

    const inBackoff = states.some(
      (s) => s.deactivated_until && new Date(s.deactivated_until) > new Date(),
    )
    if (inBackoff) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="cursor-help">
              <Clock className="mr-1 h-3 w-3" />
              Backoff
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            This proxy is temporarily unavailable due to recent errors
          </TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="cursor-help text-green-600">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          This proxy is active and available for use
        </TooltipContent>
      </Tooltip>
    )
  }

  const getPriorityBadge = (priority: number) => {
    return priority >= 10 ? (
      <Badge variant="default">High ({priority})</Badge>
    ) : (
      <Badge variant="outline">Default ({priority})</Badge>
    )
  }

  return (
    <TooltipProvider>
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Proxy URLs" />

        <div className="flex h-full flex-1 flex-col gap-4 p-4">
          {/* Header with stats and add button */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">
                Proxy URLs
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage your team's proxy URLs and monitor their status across
                platforms
              </p>
            </div>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Proxy URL
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                  <DialogTitle>Add New Proxy URL</DialogTitle>
                  <DialogDescription>
                    Add a new proxy URL to your team's collection. The URL
                    should include the protocol and port.
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="url">Proxy URL</Label>
                    <Input
                      id="url"
                      placeholder="http://proxy.example.com:8080"
                      value={data.url}
                      onChange={(e) => setData("url", e.target.value)}
                      required
                    />
                    {errors.url && (
                      <p className="text-sm text-red-600">{errors.url}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provider">Provider</Label>
                    <Input
                      id="provider"
                      placeholder="Enter proxy provider name"
                      value={data.provider}
                      onChange={(e) => setData("provider", e.target.value)}
                      required
                    />
                    {errors.provider && (
                      <p className="text-sm text-red-600">{errors.provider}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={data.priority.toString()}
                      onValueChange={(value) =>
                        setData("priority", parseInt(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.priority && (
                      <p className="text-sm text-red-600">{errors.priority}</p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsAddDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing ? "Adding..." : "Add Proxy URL"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Proxies
                </CardTitle>
                <Shield className="text-muted-foreground h-4 w-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_count}</div>
                <p className="text-muted-foreground text-xs">
                  {stats.recent_count} added this week
                </p>
              </CardContent>
            </Card>

            {Object.entries(stats.provider_counts).map(([provider, count]) => (
              <Card key={provider}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium capitalize">
                    {provider}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{count}</div>
                  <p className="text-muted-foreground text-xs">
                    {((count / stats.total_count) * 100).toFixed(0)}% of total
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Proxy URLs by Provider */}
          <div className="space-y-6">
            {Object.entries(proxy_urls_by_provider).map(
              ([provider, proxies]) => (
                <Card key={provider}>
                  <CardHeader>
                    <CardTitle className="capitalize">
                      {provider} Proxies ({proxies.length})
                    </CardTitle>
                    <CardDescription>
                      Proxy URLs from the {provider} provider
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {proxies.map((proxy) => (
                        <div
                          key={proxy.id}
                          className="flex items-center justify-between rounded-lg border p-4"
                        >
                          <div className="space-y-1">
                            <Link
                              href={proxyUrlPath(proxy.id)}
                              className="font-mono text-sm hover:underline"
                            >
                              {proxy.url}
                            </Link>
                            <div className="flex items-center space-x-2">
                              {getStatusBadge(proxy.proxy_platform_states)}
                              {getPriorityBadge(proxy.priority)}
                            </div>
                            {proxy.proxy_platform_states.length > 0 && (
                              <div className="text-muted-foreground text-xs">
                                Platforms:{" "}
                                {proxy.proxy_platform_states
                                  .map((s) => s.platform)
                                  .join(", ")}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className="space-y-1 text-right">
                              <div className="text-muted-foreground text-sm">
                                Added {formatDate(proxy.created_at)}
                              </div>
                              {proxy.proxy_platform_states.length > 0 && (
                                <div className="text-muted-foreground text-xs">
                                  Total usage:{" "}
                                  {proxy.proxy_platform_states.reduce(
                                    (sum, s) => sum + s.usage_count,
                                    0,
                                  )}
                                </div>
                              )}
                            </div>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(proxy.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ),
            )}
          </div>

          {/* Empty state */}
          {proxy_urls.length === 0 && (
            <Card className="py-12 text-center">
              <CardContent>
                <Shield className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-medium">No proxy URLs yet</h3>
                <p className="text-muted-foreground mb-4">
                  Get started by adding your first proxy URL to your team's
                  collection.
                </p>
                <Dialog
                  open={isAddDialogOpen}
                  onOpenChange={setIsAddDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Your First Proxy URL
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </CardContent>
            </Card>
          )}
        </div>
      </AppLayout>
    </TooltipProvider>
  )
}
